import React, { useEffect, useState, useRef } from 'react';
import { Page, Card, ResourceList, ResourceItem, Avatar, TextField, Button, Badge, Banner, Spinner } from '@shopify/polaris';
import { authenticatedFetch } from '../utils/api';
import { config } from '../constants';

const API_BASE = config.API_BASE;

// Utility function to get time ago string
function getTimeAgo(timestamp) {
  if (!timestamp) return '';
  const now = new Date();
  const then = new Date(timestamp);
  const diff = Math.floor((now - then) / 1000); // seconds
  if (isNaN(diff)) return '';
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff/60)}m`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h`;
  return `${Math.floor(diff/86400)}d`;
}

export default function AgentDashboard({ storeId }) {
  const [showOriginalMap, setShowOriginalMap] = useState({});
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [listLoading, setListLoading] = useState(false); // loading for conversation list
  const [msgLoading, setMsgLoading] = useState(false); // loading for messages
  const [refreshing, setRefreshing] = useState(false);
const [manualRefreshing, setManualRefreshing] = useState(false);
const [isAutoPolling, setIsAutoPolling] = useState(false);
const [seenConversations, setSeenConversations] = useState(new Set());
  const [lastSeenTimestamps, setLastSeenTimestamps] = useState({}); // { rowKey: lastSeenTimestamp }
  const [lastSeenMsgCount, setLastSeenMsgCount] = useState({}); // { rowKey: lastSeenCount }
  const [currentMsgCount, setCurrentMsgCount] = useState({}); // { rowKey: currentCount }
  const prevMessagesRef = useRef({}); // Track last message count per conversation
  const inputRef = useRef(null);

    // Fetch conversations from backend
  useEffect(() => {
  const intervalId = setInterval(() => {
    setIsAutoPolling(true);
    setRefreshing(true);
  }, 5000); // Poll every 5 seconds
  return () => clearInterval(intervalId);
}, []);

useEffect(() => {
    async function fetchConversations() {
      if (!isAutoPolling) setListLoading(true);
      try {
        const res = await authenticatedFetch(`${API_BASE}/livechat/list?shop=${encodeURIComponent(storeId || '')}`);
        const data = await res.json();
        const convos = (data && data.conversations) || [];
        setConversations(convos);
        // Update selected to the new object if present
        if (selected && selected.rowKey) {
          const updated = convos.find(c => c.rowKey === selected.rowKey);
          // Only update if something actually changed (shallow compare rowKey and lastMessage timestamp)
          if (
            updated &&
            (selected.rowKey !== updated.rowKey ||
              (selected.lastMessage?.timestamp !== updated.lastMessage?.timestamp))
          ) {
            setSelected(updated);
          }
        }
      } catch (err) {
        setConversations([]);
      }
      setListLoading(false);
      setRefreshing(false);
      setManualRefreshing(false);
      setIsAutoPolling(false);
    }
    fetchConversations();
  }, [storeId, refreshing]);

  // Fetch messages for selected conversation & poll for new ones
  useEffect(() => {
    let intervalId;
    async function fetchMessages(showLoading = true) {
      if (!selected) return;
      if (showLoading) setMsgLoading(true);
      try {
        const res = await authenticatedFetch(`${API_BASE}/livechat/history?shop=${encodeURIComponent(selected.partitionKey)}&conversationId=${encodeURIComponent(selected.rowKey)}&role=admin`);
        const data = await res.json();
        setMessages(data.messages || []);
      } catch (err) {
        setMessages([]);
      }
      if (showLoading) setMsgLoading(false);
    }
    fetchMessages();
    if (selected) {
      intervalId = setInterval(() => fetchMessages(false), 3000);
    }
    return () => intervalId && clearInterval(intervalId);
  }, [selected]);

  // Send agent reply
  const handleSend = async () => {
    console.log('Send button clicked, reply :', reply);
    if (!reply.trim() || !selected) return;
    try {
      await authenticatedFetch(`${API_BASE}/livechat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop: selected.partitionKey,
          conversationId: selected.rowKey,
          sender: 'agent',
          text: reply
        })
      });
      setReply("");
      // Refresh messages after sending (commented out, rely on polling)
      // const res = await authenticatedFetch(`${API_BASE}/livechat/history?shop=${encodeURIComponent(selected.partitionKey)}&conversationId=${encodeURIComponent(selected.rowKey)}&role=admin`);
      // const data = await res.json();
      // setMessages(data.messages || []);
      // Focus input after send
      setTimeout(() => inputRef.current && inputRef.current.focus(), 100);
    } catch (err) {}
    setMsgLoading(false);
  };

  // Autofocus input when chat is selected
  useEffect(() => {
    if (selected && selected.messages && selected.messages.length > 0) {
      const lastMsg = selected.messages[selected.messages.length - 1];
      setLastSeenTimestamps(prev => {
        const updated = { ...prev, [selected.rowKey]: lastMsg.timestamp };
        console.log('[lastSeenTimestamps updated]', updated);
        return updated;
      });
    }
    if (selected && inputRef.current) {
      setTimeout(() => inputRef.current && inputRef.current.focus(), 100);
    }
  }, [selected, selected?.messages]);

  // Activate (join) chat
  const handleActivateChat = async () => {
    if (!selected) return;
    await authenticatedFetch(`${API_BASE}/livechat/activate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        shop: selected.partitionKey,
        conversationId: selected.rowKey,
      })
    });
    // Immediately fetch updated conversations and update selected
    try {
      const res = await authenticatedFetch(`${API_BASE}/livechat/list?shop=${encodeURIComponent(storeId || '')}`);
      const data = await res.json();
      const convos = (data && data.conversations) || [];
      setConversations(convos);
      const updated = convos.find(c => c.rowKey === selected.rowKey);
      if (updated) setSelected(updated);
    } catch (err) {}
    setRefreshing(false);
  };


  // Close chat
  const handleCloseChat = async () => {
    if (!selected) return;
    await authenticatedFetch(`${API_BASE}/livechat/close`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        shop: selected.partitionKey,
        conversationId: selected.rowKey,
        endedBy: 'agent'
      })
    });
    setRefreshing(true);
  };

  return (
    <Page title="Live Chat" fullWidth>
      <div style={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 33%', minWidth: 280, minHeight: 700, height: 700 }}>
          <Card title="Open Conversations" sectioned>
            <div style={{ textAlign: 'right', marginBottom: 8 }}>
              <Button variant="primary" onClick={manualRefreshing ? undefined : () => { setIsAutoPolling(false); setSeenConversations(new Set()); setManualRefreshing(true); setRefreshing(true); }} loading={manualRefreshing}>Refresh</Button>
            </div>
            <div style={{ height: 570, overflowY: 'auto' }}>
              <ResourceList
                loading={false}
                resourceName={{ singular: 'conversation', plural: 'conversations' }}
                items={conversations}
                renderItem={(item) => {
                  const { rowKey, customerId, status, lastMessage, unreadUserCount = 0 } = item;
                  const isSelected = selected && selected.rowKey === rowKey;
                  const isSeen = seenConversations.has(rowKey);
                  const displayUnread = isSeen ? 0 : unreadUserCount; // use this for badge/highlight

                  // Use lastMessage for preview and time
                  const previewText = lastMessage ? (lastMessage.translated?.admin || lastMessage.text) : '';
                  const lastMsgTime = lastMessage ? lastMessage.timestamp : null;

                  // WhatsApp-style unread highlight and badge
                  let highlightBg = '#fff';
                  let highlightBorder = 'none';
                  if (displayUnread > 0 && !isSelected) {
                    highlightBg = '#FFF8E1';
                    highlightBorder = '6px solid #FFA000';
                  } else if (isSelected) {
                    highlightBg = '#E3EFFE';
                  }

                  // Status dot color
                  const statusColor = status === 'active' ? '#3DCC5C' : status === 'pending' ? '#FFC453' : '#B4B8C7';

                  return (
                    <ResourceItem
                      id={rowKey}
                      accessibilityLabel={`View conversation with ${customerId}`}
                      onClick={async () => {
                        await authenticatedFetch(`${API_BASE}/livechat/seen`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            shop: item.partitionKey,
                            conversationId: item.rowKey,
                            timestamp: Date.now()
                          })
                        });
                        setSeenConversations(prev => new Set(prev).add(item.rowKey));
                        setSelected(item);
                      }}
                      persistActions
                      style={{
                        background: highlightBg,
                        borderLeft: highlightBorder,
                        borderRadius: 8,
                        marginBottom: 6,
                        cursor: 'pointer',
                        boxShadow: isSelected ? '0 0 0 2px #2A5BD7' : undefined,
                        transition: 'background 0.3s, border 0.3s'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Avatar customer size="large" name={customerId || 'User'} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontWeight: 600 }}>{customerId || 'User'}</span>
                        <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                          {lastMessage ? (lastMessage.translated?.admin || lastMessage.text) : ''}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: statusColor, marginRight: 4 }} />
                          <span style={{ fontSize: 12, color: '#888' }}>{status?.charAt(0).toUpperCase() + status?.slice(1)}</span>
                        </div>
                      </div>
                      {displayUnread > 0 && !isSelected && (
                        <span style={{
                          background: '#3DCC5C',
                          color: '#fff',
                          borderRadius: 12,
                          padding: '2px 12px',
                          fontWeight: 700,
                          fontSize: 15,
                          marginLeft: 8,
                          minWidth: 28,
                          textAlign: 'center',
                          display: 'inline-block',
                          boxShadow: '0 1px 4px #3DCC5C33'
                        }}>{displayUnread}</span>
                      )}
                      <div style={{ fontSize: 12, color: '#bbb', minWidth: 32, textAlign: 'right' }}>
                        {lastMessage ? getTimeAgo(lastMessage.timestamp) : ''}
                      </div>
                      </div>
                    </ResourceItem>
                  );
                }}
              />
            </div>
          </Card>
        
        </div>
        <div style={{ flex: '2 1 67%', minWidth: 400, minHeight: 700, height: 700 }}>
          <Card title="Messages" sectioned>
            {msgLoading ? (
              <div style={{ textAlign: 'center', padding: 32 }}><Spinner size="large" /></div>
            ) : selected ? (
              <>
                <div style={{ background: '#F8F9FB', borderRadius: 10, padding: 24, minHeight: 160, marginBottom: 16, height: 480, overflowY: 'auto' }}>
                  {messages.length === 0 ? (
                    <div style={{ color: '#b4b8c7', fontSize: 16, textAlign: 'center', marginTop: 40 }}>
                      No messages in this conversation.
                    </div>
                  ) : (
                    messages.map((msg, i) => {
                      const isAgent = msg.sender === 'agent';
                      const isUser = msg.sender === 'user';
                      const isSystem = msg.sender === 'system';
                      const isTranslated = msg.translated;
                      const showOriginal = showOriginalMap[i];
                      return (
                        <div key={i} style={{ marginBottom: 12, display: 'flex', justifyContent: isAgent ? 'flex-end' : 'flex-start' }}>
                          <div style={{
                            background: isAgent ? '#2a5bd7' : isSystem ? '#f4f5f7' : '#f6f8fa',
                            color: isAgent ? '#fff' : '#23272f',
                            borderRadius: 16,
                            padding: '10px 18px',
                            maxWidth: 440,
                            fontSize: 15,
                            fontWeight: isSystem ? 500 : 400,
                            boxShadow: isAgent ? '0 1px 7px #2a5bd71a' : 'none',
                            alignSelf: isAgent ? 'flex-end' : 'flex-start',
                            position: 'relative',
                          }}>
                            {isSystem && (
                              <Badge status="warning">System</Badge>
                            )}
                            {isUser && msg.isTranslated && msg.text !== msg.originalText ? (
                              <>
                                <span>
                                  {!showOriginal ? msg.text : msg.originalText}
                                </span>
                                <div style={{ fontSize: 12, color: '#2a5bd7', marginTop: 2, cursor: 'pointer', display: 'inline-block' }}>
                                  <span style={{ marginRight: 8, color: '#888' }}>Translated</span>
                                  <Button
                                    plain
                                    monochrome
                                    size="slim"
                                    onClick={() => setShowOriginalMap(prev => ({ ...prev, [i]: !prev[i] }))}
                                    style={{ padding: 0, fontSize: 12, textDecoration: 'underline', minHeight: 0 }}
                                  >
                                    {showOriginal ? 'Show translation' : 'Show original'}
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <span>{msg.text}</span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                {selected.status === 'pending' && (
                  <Banner status="info" title="Join this conversation to start chatting.">
                    <Button primary onClick={handleActivateChat}>Join conversation</Button>
                  </Banner>
                )}
                <form style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, width: '100%' }} onSubmit={e => { e.preventDefault(); handleSend(); }}>
                  <div style={{ flex: 1 }}>
                    <TextField
                      ref={inputRef}
                      labelHidden
                      placeholder="Type your reply..."
                      value={reply}
                      onChange={setReply}
                      disabled={selected.status !== 'active' || msgLoading || messages.some(m => m.sender === 'system' && m.text?.toLowerCase().includes('ended by user'))}
                      autoComplete="off"
                      fullWidth
                    />
                  </div>
                  <Button
                    variant='primary'
                    type="submit"
                    onClick={handleSend}
                    disabled={selected.status !== 'active' || msgLoading || !reply.trim() || messages.some(m => m.sender === 'system' && m.text?.toLowerCase().includes('ended by user'))}
                  >
                    Send
                  </Button>
                  {selected.status === 'active' && (
                    <Button variant="primary" tone="critical" onClick={handleCloseChat}>
                      End Chat
                    </Button>
                  )}
                </form>
              </>
            ) : (
              <div style={{ color: '#b4b8c7', fontSize: 16, textAlign: 'center', marginTop: 80 }}>
                Select a conversation to view messages.
              </div>
            )}
          </Card>
        </div>
      </div>
    </Page>
  );
}
